import { cn } from "@/lib/cn";

/**
 * SegmentedBar — a single horizontal bar divided into proportional
 * segments with labels below. Used for two-to-four-way composition
 * data (gender, orientation, etc.) where a full bar chart would
 * feel over-engineered and a pie chart would feel decorative.
 */
export const SegmentedBar = ({ segments = [], testId, ariaLabel }) => {
  if (!segments.length) return null;
  const total = segments.reduce((s, x) => s + x.value, 0);

  // Progressive opacity: first segment is the darkest, subsequent segments
  // step lighter. This creates a calm hierarchy without arbitrary color.
  const opacityFor = (i) => {
    const steps = [1, 0.55, 0.32, 0.2];
    return steps[i] ?? 0.15;
  };

  return (
    <div data-testid={testId || "segmented-bar"} role="img" aria-label={ariaLabel}>
      <div className="flex h-[10px] w-full overflow-hidden rounded-full bg-border">
        {segments.map((seg, i) => (
          <div
            key={seg.label}
            data-testid={`segment-${seg.label.replace(/\s+/g, "-").toLowerCase()}`}
            style={{
              width: `${(seg.value / total) * 100}%`,
              backgroundColor: "currentColor",
              opacity: opacityFor(i),
            }}
            className={cn("h-full text-foreground", i > 0 && "border-l border-background")}
          />
        ))}
      </div>
      <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2">
        {segments.map((seg, i) => (
          <div
            key={seg.label}
            className="flex items-baseline gap-2"
            data-testid={`segment-legend-${seg.label.replace(/\s+/g, "-").toLowerCase()}`}
          >
            <span
              aria-hidden="true"
              className="h-2 w-2 shrink-0 rounded-full bg-foreground"
              style={{ opacity: opacityFor(i) }}
            />
            <span className="text-sm font-medium tabular-nums text-foreground">
              {seg.value}%
            </span>
            <span className="text-sm text-muted-foreground">{seg.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SegmentedBar;
